import { FC, useMemo, useCallback, useState, forwardRef, useContext } from 'react';
import { AppBar, Box, Button, Chip, Dialog, Divider, IconButton, List, Paper, Slide, styled, Toolbar, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faCodeBranch, faEarthOceania, faEquals, faInputText, faLinkHorizontal, faListOl, faObjectGroup, faRepeat, faSlash, faSquareList, faTableList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, IHasChildren, InputOnlyField, IValidationError, Text } from "@ngt/forms";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FieldType, IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata } from "@/api/dtos";
import { ConfigurationType, IConfigurationItem, ISourceDataConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";
import ConfigurationFormEditContext, { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { Condition, ConditionMode, useFormActions, useScopedField } from '@ngt/forms-core';
import { TransitionProps } from '@mui/material/transitions';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { SourceDataType } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataFormFieldValue from './SourceDataFormFieldValue';

export const SourceDataIconBox = styled(Box)(({theme}) => ({
    fontSize: '1.5rem',
    marginRight: theme.spacing(2)
}));

export const SourceDataDividerBox = styled(Box)(({theme}) => ({
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
}));

export const SourceDataBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

export const SourceDataTitle = styled(Typography)(({theme}) => ({
    margin: theme.spacing(2, 2, 2, 2)
}));

export const SourceDataFlexBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    minHeight: 52
}));

export const SourceDataInfoChip = styled(Chip)(({theme}) => ({
    marginLeft: 'auto',
}));

export interface ISourceDataInfoProps {
    type?: SourceDataType;
    eventKey?: string;
    eventRepeat?: number;
    eventMetadata?: IEventMetadata;
    formKey?: string;
    formMetadata?: IFormMetadata;
    formVersionKey?: string;
    formVersionMetadata?: IFormVersionMetadata;
    formSectionKey?: string;
    formSectionRepeat?: number;
    formSectionMetadata?: IFormSectionMetadata;
    formFieldKey?: string;
    formFieldMetadata?: IFormFieldMetadata;
    formFieldTypeLabel?: string;
    formFieldValueKey?: string;
    formFieldValueMetadata?: IFormFieldValueMetadata;
    formFieldValue?: string;
    dividers?: boolean;
}

const SourceDataInfo: FC<ISourceDataInfoProps> = ({
    eventKey,
    eventRepeat,
    eventMetadata,
    formFieldKey,
    formFieldMetadata,
    formFieldTypeLabel,
    formFieldValue,
    formFieldValueKey,
    formFieldValueMetadata,
    formKey,
    formMetadata,
    formSectionKey,
    formSectionRepeat,
    formSectionMetadata,
    formVersionKey,
    formVersionMetadata,
    dividers
}) => {

    return (
        <>
            {
                eventKey && (
                    <SourceDataFlexBox>
                        <SourceDataIconBox>
                            <FontAwesomeIcon fixedWidth icon={faCalendarDays} />
                        </SourceDataIconBox>
                        {eventMetadata?.name ?? 'Unknown Event'}
                        {
                            eventMetadata && eventMetadata.repeating && eventRepeat !== undefined && (
                                <SourceDataInfoChip 
                                    label={eventRepeat}
                                    icon={<FontAwesomeIcon icon={faRepeat} />}
                                    size="small"
                                    color="primary"
                                />
                            )
                        }
                    </SourceDataFlexBox>
                )
            }
            {
                formKey && (
                    <>
                        {
                            eventKey && dividers && (
                                <SourceDataDividerBox>
                                    <Divider />
                                </SourceDataDividerBox>
                            )
                        }
                        <SourceDataFlexBox>
                            <SourceDataIconBox>
                                <FontAwesomeIcon fixedWidth icon={faSquareList} />
                            </SourceDataIconBox>
                            {formMetadata?.name ?? 'Unknown Form'}
                        </SourceDataFlexBox>
                        {
                            formVersionKey && (
                                <>
                                    {
                                        dividers && (
                                            <SourceDataDividerBox>
                                                <Divider />
                                            </SourceDataDividerBox>
                                        )
                                    }
                                    <SourceDataFlexBox>
                                        <SourceDataIconBox>
                                            <FontAwesomeIcon fixedWidth icon={faCodeBranch} />
                                        </SourceDataIconBox>
                                        {formVersionMetadata?.name ?? 'Unknown Version'}
                                    </SourceDataFlexBox>
                                    {
                                        formSectionKey && (
                                            <>
                                                {
                                                    dividers && (
                                                        <SourceDataDividerBox>
                                                            <Divider />
                                                        </SourceDataDividerBox>
                                                    )
                                                }
                                                <SourceDataFlexBox>
                                                    <SourceDataIconBox>
                                                        <FontAwesomeIcon fixedWidth icon={formSectionMetadata?.repeating ? faTableList : faObjectGroup} />
                                                    </SourceDataIconBox>
                                                    {formSectionMetadata?.name ?? 'Unknown Section'}
                                                    <SourceDataInfoChip 
                                                        label={formSectionMetadata?.repeating ? 'Collection' : 'Group'}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                    {
                                                        formSectionMetadata && formSectionMetadata.repeating && formSectionRepeat !== undefined && (
                                                            <SourceDataInfoChip 
                                                                label={formSectionRepeat}
                                                                icon={<FontAwesomeIcon icon={faRepeat} />}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        )
                                                    }
                                                </SourceDataFlexBox>
                                            </>
                                        )
                                    }
                                    {
                                        formFieldKey && (
                                            <>
                                                {
                                                    dividers && (
                                                        <SourceDataDividerBox>
                                                            <Divider />
                                                        </SourceDataDividerBox>
                                                    )
                                                }
                                                <SourceDataFlexBox>
                                                    <SourceDataIconBox>
                                                        <FontAwesomeIcon fixedWidth icon={faInputText} />
                                                    </SourceDataIconBox>
                                                    {formFieldMetadata?.name ?? 'Unknown Field'}
                                                    <SourceDataInfoChip 
                                                        label={formFieldTypeLabel}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </SourceDataFlexBox>
                                                {
                                                    dividers && (
                                                        <SourceDataDividerBox>
                                                            <Divider />
                                                        </SourceDataDividerBox>
                                                    )
                                                }
                                                <SourceDataFlexBox>
                                                    <SourceDataIconBox>
                                                        <FontAwesomeIcon fixedWidth icon={faEquals} />
                                                    </SourceDataIconBox>
                                                    <SourceDataFormFieldValue fieldType={formFieldMetadata?.fieldType} formFieldValue={formFieldValue} noBox />
                                                    {
                                                        formFieldValueMetadata?.id !== undefined && (
                                                            <SourceDataInfoChip 
                                                                label={`ID: ${formFieldValueMetadata?.id}`}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        )
                                                    }
                                                </SourceDataFlexBox>
                                            </>
                                        )
                                    }
                                </>
                            )
                        }
                    </>
                )
            }
        </>
    )
}

export default SourceDataInfo;